// T134 Artefact 2 — JSON-LD generators. Pure functions returning plain objects
// that callers embed via `<script type="application/ld+json">` in route VIEWS
// (RR v7's `meta` cannot emit script bodies). All copy is passed in from
// loaders/i18n — no hardcoded strings beyond stable brand identity.
import type { Lang } from "~/config/route-config";
import { BRAND_ENTITY_ORIGIN } from "~/config/hosts";
import { SOCIAL_PROFILE_URLS } from "~/config/social";
import { absoluteFromPath, DEFAULT_OG_IMAGE, SITE_NAME, SITE_ORIGIN } from "./site";

/** Loosely-typed JSON-LD node. */
export type JsonLd = Record<string, unknown>;

// Stable brand identity — not i18n copy (intentional exception to the
// "no hardcoded strings" rule above; these don't vary by locale).
const FOUNDER_NAME = "Gala Calero";
const LOGO_URL = absoluteFromPath("/icon-512.png");

/**
 * The founder's canonical `Person` entity, published on her own site. Used as
 * the founder's `@id` rather than a `sameAs` entry: `sameAs` lists profile
 * pages ("also found at"), while `@id` asserts identity ("is this entity"),
 * which is what we mean. It lets the two properties corroborate each other —
 * galacalero.com declares `worksFor` → TheFittClub and this points back (T424).
 */
const FOUNDER_PERSON_ID = "https://galacalero.com/#person";

// T424 — `BRAND_ENTITY_ORIGIN` now lives in `app/config/hosts.ts` with the other
// two host concepts; see there for why identity must not follow the serving host.

/**
 * Stable identifiers for the two root nodes. Everything else REFERENCES these
 * instead of re-declaring them — before T424 four generators each emitted their
 * own inline Organization, so engines saw five unlinked companies and nothing
 * external could point at the entity at all.
 *
 * Module-private on purpose: callers embed whole nodes, never a bare id, and the
 * dead-export gate is right to reject an export nothing consumes. Export when a
 * caller genuinely needs to reference the entity from outside.
 */
const ORGANIZATION_ID = `${BRAND_ENTITY_ORIGIN}/#organization`;
const WEBSITE_ID = `${BRAND_ENTITY_ORIGIN}/#website`;

/**
 * A bare reference to the canonical Organization node.
 *
 * Safe because `root.tsx` renders the whole document — `<Outlet />` included —
 * and emits `organizationJsonLd()` in every one, so the referenced node is
 * always present in the same page's graph. If a surface ever ships an Article
 * or Course node OUTSIDE that document, it must emit an `@graph` carrying both
 * nodes instead of a bare ref, or `publisher` dangles and the rich result is
 * lost.
 */
const orgRef = () => ({ "@id": ORGANIZATION_ID });

function inLanguage(locale: Lang): string {
  return locale === "en" ? "en" : "es";
}

/**
 * Organization node — emitted once in `root.tsx` so every page carries it.
 *
 * `sameAs` defaults to the brand's real profiles, from `config/social.ts` — the
 * same declaration the public footer renders its two anchors from, so the page a
 * woman clicks and the entity an engine reads cannot name different accounts.
 * The parameter stays for a caller that needs a narrower list.
 *
 * (This docblock said `sameAs` defaults to empty "while the social URLs are still
 * placeholders". That stopped being true on 2026-06-15 and nobody noticed, which
 * is its own small argument for one declaration.)
 */
export function organizationJsonLd(opts?: { sameAs?: string[]; description?: string }): JsonLd {
  const sameAs = opts?.sameAs ?? SOCIAL_PROFILE_URLS;
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    // 2026-11 — the name collides with the FITT training principle and the programme's
    // with a vitamin brand; the spellings an engine meets in the wild resolve to us.
    alternateName: ["The Fitt Club", "TFC"],
    url: SITE_ORIGIN,
    logo: LOGO_URL,
    // No `sameAs` here on purpose: these are the BRAND's accounts, so
    // listing them on the person would assert that those accounts are her — the
    // same "also found at" vs "is this entity" conflation the `@id` above exists
    // to avoid. Her canonical Person entity owns her own profile list.
    founder: { "@type": "Person", "@id": FOUNDER_PERSON_ID, name: FOUNDER_NAME },
  };
  if (opts?.description) node.description = opts.description;
  if (sameAs.length > 0) node.sameAs = sameAs;
  return node;
}

export interface FounderPersonInput {
  /** Her one-paragraph biography in the page's language (the signed V8). */
  description: string;
  /** What she writes about, in the page's language — `knowsAbout` is how an engine files an author. */
  knowsAbout: string[];
}

/**
 * The founder as a full node on a page she signs (2026-11, AI positioning): the same
 * `@id` as galacalero.com so the two sites corroborate, plus the biography and
 * `knowsAbout` an assistant reads to answer «¿quién hay detrás?». `sameAs` stays on
 * her own site (R-T424-4): this node adds what she says, not where she is.
 */
export function founderPersonJsonLd(input: FounderPersonInput): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": FOUNDER_PERSON_ID,
    name: FOUNDER_NAME,
    alternateName: "Galadriel Calero",
    url: "https://galacalero.com",
    jobTitle: "Founder",
    worksFor: orgRef(),
    description: input.description,
    knowsAbout: input.knowsAbout,
  };
}

/** WebSite node with a SearchAction stub (future on-site search). */
export function websiteJsonLd(locale: Lang): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_ORIGIN,
    inLanguage: inLanguage(locale),
    publisher: orgRef(),
  };
}

export interface BreadcrumbItem {
  name: string;
  /** Root-relative or absolute URL. */
  url: string;
}

/** BreadcrumbList — one `ListItem` per crumb, 1-indexed. */
export function breadcrumbJsonLd(items: BreadcrumbItem[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteFromPath(item.url),
    })),
  };
}

export interface CourseInput {
  name: string;
  description: string;
  locale: Lang;
  url: string;
  /** ISO 8601 duration, e.g. "P6W" (6 weeks). */
  timeRequired?: string;
  educationalLevel?: string;
  coursePrerequisites?: string;
}

/** Course node for public module preview pages (consumed by T136/T53/T36). */
export function courseJsonLd(input: CourseInput): JsonLd {
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: input.name,
    description: input.description,
    inLanguage: inLanguage(input.locale),
    url: absoluteFromPath(input.url),
    provider: orgRef(),
  };
  if (input.timeRequired) node.timeRequired = input.timeRequired;
  if (input.educationalLevel) node.educationalLevel = input.educationalLevel;
  if (input.coursePrerequisites) node.coursePrerequisites = input.coursePrerequisites;
  return node;
}

export interface ProductInput {
  name: string;
  description: string;
  url: string;
  image?: string;
  /** Decimal string, e.g. "49.00". */
  price?: string;
  priceCurrency?: string;
  availability?: string;
}

/** Product node for public capsule preview / pricing tiers (T36, T53). */
export function productJsonLd(input: ProductInput): JsonLd {
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    url: absoluteFromPath(input.url),
    image: absoluteFromPath(input.image ?? DEFAULT_OG_IMAGE),
    brand: { "@type": "Brand", name: SITE_NAME },
  };
  if (input.price) {
    node.offers = {
      "@type": "Offer",
      price: input.price,
      priceCurrency: input.priceCurrency ?? "EUR",
      availability: input.availability ?? "https://schema.org/InStock",
      url: absoluteFromPath(input.url),
    };
  }
  return node;
}

export interface FaqItem {
  question: string;
  answer: string;
}

/** FAQPage node — consumed by T136 `/faq` and T53 posts with FAQ blocks. */
export function faqJsonLd(items: FaqItem[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

export interface AboutPageInput {
  name: string;
  description: string;
  locale: Lang;
  url: string;
}

/** AboutPage node — consumed by T136 `/metodo`. */
export function aboutPageJsonLd(input: AboutPageInput): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: input.name,
    description: input.description,
    inLanguage: inLanguage(input.locale),
    url: absoluteFromPath(input.url),
    publisher: orgRef(),
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  locale: Lang;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
}

/** Article node — consumed by T53 blog posts. */
export function articleJsonLd(input: ArticleInput): JsonLd {
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    inLanguage: inLanguage(input.locale),
    url: absoluteFromPath(input.url),
    image: absoluteFromPath(input.image ?? DEFAULT_OG_IMAGE),
    // The founder's `@id` goes ONLY on the default branch. An explicit
    // `authorName` is a different person, and asserting her identifier for them
    // would state that they ARE her. Without this split every indexable guide
    // page carried two Gala Calero nodes — the identified one inside
    // `Organization.founder` and an anonymous one here (T424 iteration 2).
    author: input.authorName
      ? { "@type": "Person", name: input.authorName }
      : { "@type": "Person", "@id": FOUNDER_PERSON_ID, name: FOUNDER_NAME },
    publisher: orgRef(),
  };
  if (input.datePublished) node.datePublished = input.datePublished;
  if (input.dateModified) node.dateModified = input.dateModified;
  return node;
}
